import { chatModelHasEffort, chatModelLabel, reasoningEffortLabel, supportsDeepResearch } from '../../utils/chatModelCapabilities';
import React, { useState } from 'react';
import { useRenderTracker } from '../../utils/perfProbe';
import styled, { keyframes } from 'styled-components';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StopIcon from '@mui/icons-material/Stop';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LanguageIcon from '@mui/icons-material/Language';
import TerminalIcon from '@mui/icons-material/Terminal';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import FolderIcon from '@mui/icons-material/Folder';
import MicNoneIcon from '@mui/icons-material/MicNone';
import CategoryIcon from '@mui/icons-material/Category';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BoltIcon from '@mui/icons-material/Bolt';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import TooltipBase, { tooltipClasses } from '@mui/material/Tooltip';
import { styled as MUIStyled } from '@mui/system';
import { ClickAwayListener } from '@mui/base';
import RealWaveform from '../chat/Waveform';
import { SIDEBAR_CONTENT_GAP, CHAT_MAX_WIDTH, MAX_AUDIO_DURATION_TEXT } from './constants';
import { usePlayground } from '../../shared/context/PlaygroundContext';
import ComposerPromptUI from './ComposerPromptUI';
import { useFeatureFlags } from '../../shared/context/FeatureFlagsContext';
import { getModelColor } from '../../shared/Constants';
import { ToggleSwitch } from '../../shared/components/ToggleSwitch';
import { clipboardImageFiles } from '../../utils/chatAttachments';

// Pasting a very large block of text into the message bar attaches it as a
// mensagem_N.txt file instead of inlining it. Huge inline pastes (e.g. a 2 MB
// JSON graph) overflow the model's context and get rejected as "mensagem muito
// grande"; as a file the text lands in /workspace/documents and the model reads
// it with tools.
const PASTE_AS_FILE_MAX_CHARS = 20_000;

// CustomTooltip - shared tooltip style
const CustomTooltip = styled(({ className, ...props }: any) => (
  <TooltipBase {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '1rem',
  },
});

// Styled Components
const InputContainer = styled.div<{ $isMobile: boolean; $sidebarWidth: number; $bottomOffset: number; $isLanding?: boolean }>`
  display: flex;
  position: ${({ $isLanding }) => $isLanding ? 'relative' : 'fixed'};
  background: transparent;
  border-radius: 25px;
  bottom: ${({ $isLanding, $bottomOffset }) => $isLanding ? 'auto' : `${$bottomOffset}px`};
  padding-bottom: ${({ $isLanding }) => $isLanding ? '0' : 'env(safe-area-inset-bottom)'};
  width: 100%;
  max-width: ${({ $isLanding, $isMobile, $sidebarWidth }) =>
    $isLanding
      ? '100%'
      : $isMobile
        ? `clamp(260px, calc(100vw - 40px), ${CHAT_MAX_WIDTH}px)`
        : `clamp(260px, calc(100vw - ${Math.round($sidebarWidth + SIDEBAR_CONTENT_GAP)}px - var(--artifact-panel-width, 0px)), ${CHAT_MAX_WIDTH}px)`};
  left: ${({ $isLanding, $isMobile, $sidebarWidth }) =>
    $isLanding
      ? 'auto'
      : $isMobile
        ? '50%'
        // --artifact-panel-width: painel de artefato aberto à direita (desktop).
        : `calc(50vw + ${Math.round($sidebarWidth / 2)}px - var(--artifact-panel-width, 0px) / 2)`};
  transform: ${({ $isLanding }) => $isLanding ? 'none' : 'translateX(-50%)'};
  flex-direction: column;
  justify-content: center;
  align-items: ${({ $isLanding }) => $isLanding ? 'stretch' : 'center'};
  ${({ $isLanding, $bottomOffset }) => !$isLanding && `
    @supports (bottom: calc(1px + env(safe-area-inset-bottom))) {
      bottom: calc(${$bottomOffset}px + env(safe-area-inset-bottom));
    }
    @supports (bottom: calc(1px + constant(safe-area-inset-bottom))) {
      bottom: calc(${$bottomOffset}px + constant(safe-area-inset-bottom));
    }
    @supports (padding-bottom: constant(safe-area-inset-bottom)) {
      padding-bottom: constant(safe-area-inset-bottom);
    }
  `}
  @media (max-width: 600px) {
    max-width: ${({ $isLanding }) => $isLanding ? '100%' : `clamp(260px, calc(100vw - 40px), ${CHAT_MAX_WIDTH}px)`};
    left: ${({ $isLanding }) => $isLanding ? 'auto' : '50%'};
  }
`;

const ScrollToBottomButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-10px);
  width: 40px;
  height: 40px;
  background-color: ${({ theme }) => theme.model_selector_bg};
  border: 1px solid ${({ theme }) => theme.model_selector_border};
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1000;

  &:hover {
    background-color: ${({ theme }) => theme.model_selector_hover_bg};
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
    transform: translateX(-50%) translateY(-12px);
  }
`;

const StyledArrowDownwardIcon = styled(ArrowDownwardIcon)`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.arrow_icon_color};
  transition: color 0.2s ease;
`;

const StylizedForm = styled.form`
  display: flex;
  flex: 1;
  width: 100%;
  align-items: stretch;
  justify-content: center;
  background-color: transparent;
  margin: 0;
  padding: 0;
`;

const UserInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: stretch;
  justify-content: center;
  gap: 14px;
`;

const SubContainer = styled.div`
  width: 100%;
  min-width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const InputSurface = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${({ theme }) => theme.input_surface_bg};
  border: 1px solid ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'};
  border-radius: 24px;
  padding: 14px 16px 10px;
  box-shadow: ${({ theme }) => theme.mode === 'dark'
    ? '0 2px 8px rgba(0,0,0,0.3)'
    : '0 2px 8px rgba(0,0,0,0.08)'};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus-within {
    border-color: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'};
    box-shadow: ${({ theme }) => theme.mode === 'dark'
      ? '0 4px 16px rgba(0,0,0,0.4)'
      : '0 4px 16px rgba(0,0,0,0.12)'};
  }

  @media (max-width: 600px) {
    border-radius: 20px;
    padding: 12px 14px 10px;
  }
`;

const WaveformContainer = styled.div`
  flex: 1 1 0;
  min-width: 0;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin: 0;
`;

const RecordingInfo = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.font_color};
  opacity: 0.7;
`;

const PartialTranscriptText = styled.div`
  font-size: 0.9rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.font_color};
  opacity: 0.6;
  font-style: italic;
  max-height: 60px;
  overflow-y: auto;
  width: 100%;
  text-align: left;
  padding: 0 4px;
`;

const MessagesContainerArea = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputTextArea = styled(TextField)`
  && {
    width: 100%;
    background-color: ${({ theme }) => theme.input_surface_bg};
    font-family: 'Sora', 'Lexend', sans-serif;
  }

  && .MuiInputBase-root {
    padding: 0;
    background-color: ${({ theme }) => theme.input_surface_bg};
    font-family: 'Sora', 'Lexend', sans-serif;
  }

  && .MuiOutlinedInput-root {
    padding: 0;
    background-color: ${({ theme }) => theme.input_surface_bg};
  }

  && .MuiOutlinedInput-notchedOutline { border: none; }
  && .Mui-focused .MuiOutlinedInput-notchedOutline { border: none; }

  && textarea {
    font-size: 1rem;
    line-height: 1.55rem;
    padding: 0;
    color: ${({ theme }) => theme.font_color};
    background-color: ${({ theme }) => theme.input_surface_bg};
    font-family: 'Sora', 'Lexend', sans-serif;
  }

  && .MuiInputBase-input {
    font-size: 1rem;
    line-height: 1.55rem;
    padding: 0;
    color: ${({ theme }) => theme.font_color};
    background-color: ${({ theme }) => theme.input_surface_bg};
    font-family: 'Sora', 'Lexend', sans-serif;
  }

  && .MuiInputBase-input::placeholder {
    color: ${({ theme }) => theme.text_secondary};
  }
`;

const InputFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 8px;
  gap: 4px;
`;

const InputActionsGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0px;

  @media (max-width: 600px) {
    gap: 0px;
  }
`;

const AttachSpan = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const StyledAttachFileIcon = styled(AttachFileIcon)`
  font-size: 1.35rem;
  color: ${({ theme }) => theme.font_color};
  opacity: 0.6;
`;

const WebSpan = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 1.35rem;
  background-color: transparent;
  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const StyledWebIcon = styled(LanguageIcon)<{ $webSearch?: boolean; $modelColor?: string }>`
  font-size: 1.35rem;
  color: ${({ $webSearch, $modelColor, theme }) => $webSearch ? ($modelColor || theme.send_button_bg) : theme.font_color};
  opacity: ${({ $webSearch }) => $webSearch ? 0.9 : 0.6};
  transition: color 0.2s ease, opacity 0.2s ease;
`;

const StyledFolderIcon = styled(FolderIcon)<{ $active?: boolean; $modelColor?: string }>`
  font-size: 1.55rem;
  color: ${({ $active, $modelColor, theme }) => $active ? ($modelColor || theme.send_button_bg) : theme.font_color};
  opacity: ${({ $active }) => $active ? 0.9 : 0.6};
  transition: color 0.2s ease, opacity 0.2s ease;
`;

const StyledDeepResearchIcon = styled(TravelExploreIcon)<{ $active?: boolean; $modelColor?: string }>`
  font-size: 1.35rem;
  color: ${({ $active, $modelColor, theme }) => $active ? ($modelColor || theme.send_button_bg) : theme.font_color};
  opacity: ${({ $active }) => $active ? 0.9 : 0.6};
  transition: color 0.2s ease, opacity 0.2s ease;
`;

const DeepResearchSpan = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const CodeSpan = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 1.35rem;
  background-color: transparent;
  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const StyledCodeIcon = styled(TerminalIcon)<{ $codeExecution?: boolean; $modelColor?: string }>`
  font-size: 1.35rem;
  color: ${({ $codeExecution, $modelColor, theme }) => $codeExecution ? ($modelColor || theme.send_button_bg) : theme.font_color};
  opacity: ${({ $codeExecution }) => $codeExecution ? 0.9 : 0.6};
  transition: color 0.2s ease, opacity 0.2s ease;
`;

const PromptSpan = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;


const StyledPromptIcon = styled(CategoryIcon)`
  font-size: 1.45rem;
  color: ${({ theme }) => theme.font_color};
  opacity: 0.6;
`;

const StyledStopIcon = styled(StopIcon)`
  font-size: 1.6rem;
  margin: 0;
  color: ${({ theme }) => theme.font_color};
`;

const StyledMicIcon = styled(MicNoneIcon)`
  font-size: 1.6rem;
  color: ${({ theme }) => theme.font_color};
  padding: 0;
  margin: 0;
  transition: transform 0.2s ease;
`;

const recordingWave = keyframes`
  0% {
    opacity: 0.5;
    transform: scale(0.88);
  }
  75%, 100% {
    opacity: 0;
    transform: scale(1.28);
  }
`;

const AudioButton = styled.button.attrs({ type: 'button' })`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 1.4rem;
  margin: 0;
  padding: 0;
  cursor: pointer;
  background-color: transparent;
  transition: background-color 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &.is-recording {
    background-color: ${({ theme }) => theme.recording_bg};
    border-color: ${({ theme }) => theme.recording_border};
  }

  &:hover.is-recording {
    background-color: ${({ theme }) => theme.recording_bg_hover};
  }

  &.is-recording::before,
  &.is-recording::after {
    content: "";
    position: absolute;
    inset: -2px;
    border: 1px solid ${({ theme }) => theme.recording_border};
    border-radius: 12px;
    pointer-events: none;
    animation: ${recordingWave} 1.6s ease-out infinite;
  }

  &.is-recording::after {
    animation-delay: 0.8s;
  }

  @media (prefers-reduced-motion: reduce) {
    &.is-recording::before,
    &.is-recording::after {
      animation: none;
      opacity: 0.32;
    }

    &.is-recording::before {
      transform: scale(1.08);
    }

    &.is-recording::after {
      opacity: 0.18;
      transform: scale(1.2);
    }
  }

  &.is-recording ${StyledMicIcon},
  &.is-recording ${StyledStopIcon} {
    color: ${({ theme }) => theme.recording_icon};
  }
`;

const StyledIconButton = styled(IconButton)<{ $modelColor?: string; $modelHover?: string }>`
  && {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background-color: ${({ $modelColor, theme }) => $modelColor || theme.send_button_bg};
    color: #fff;
    margin: 0;
    padding: 0;
    transition: background-color 0.2s ease;
  }

  && svg {
    font-size: 1.35rem;
  }

  &&:hover {
    background-color: ${({ $modelHover, theme }) => $modelHover || theme.send_button_hover};
  }

  &&:disabled {
    background-color: ${({ theme }) => theme.send_button_disabled};
    box-shadow: none;
    cursor: not-allowed;
  }
`;

// Fica ao lado do botão de parar no mesmo grupo (gap 0): a margem separa os
// dois círculos, que têm a mesma cor.
const EnqueueButton = styled(StyledIconButton)`
  &&& {
    margin-right: 8px;
  }
`;

// Model selector styled components
const ModelSelectorButton = styled.div`
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: 600;
  font-size: 0.85rem;
  font-family: 'Noka', sans-serif;
  color: ${({ theme }) => theme.text_secondary};
  padding: 2px 0 2px 4px;
  border-radius: 6px;
  background: transparent;
  border: none;
  transition: color 0.2s ease;
  &:hover {
    color: ${({ theme }) => theme.font_color};
  }
`;

const ExpandDropdownIcon = MUIStyled(ExpandMoreIcon)` font-size: 0.95rem; `;
const SelectedModelCheckIcon = MUIStyled(CheckCircleRoundedIcon)` font-size: 1.25rem; `;
const UnselectedModelRadioIcon = MUIStyled(RadioButtonUncheckedRoundedIcon)` font-size: 1.25rem; `;

const ModelDropdownMenu = styled.div`
  position: absolute;
  z-index: 1200;
  bottom: calc(100% + 4px);
  right: 0;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.user_menu_border};
  background-color: ${({ theme }) => theme.sidebar_background_solid};
  min-width: 20rem;
  max-width: 30rem;
  max-height: 60vh;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const ModelDropdownItem = styled.div`
  display: flex;
  cursor: pointer;
  line-height: 1.25rem;
  font-family: 'Noka', sans-serif;
  padding: 10px 14px;
  gap: 0.5rem;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.15s ease;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const ModelDropdownItemText = styled.div`
  flex-grow: 1;
  text-align: left;
  display: flex;
  flex-direction: column;
`;

const ModelDropdownItemDescription = styled.div`
  color: ${({ theme }) => theme.menu_item_description_color};
  max-width: 18rem;
  font-size: 0.85rem;
`;

const ModelDropdownTabContent = styled.div`
  max-height: calc(60vh - 10px);
  overflow-y: auto;
`;

const PlusButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.15s ease;
  padding: 0;
  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
  &:hover > svg {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const PlusIcon = styled(AddRoundedIcon)`
  font-size: 1.4rem;
  opacity: 0.5;
  transition: opacity 0.15s ease;
`;

const PlusMenu = styled.div`
  position: absolute;
  z-index: 1200;
  bottom: calc(100% + 6px);
  left: 0;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.user_menu_border};
  background-color: ${({ theme }) => theme.sidebar_background_solid};
  min-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.mode === 'dark'
    ? '0 4px 16px rgba(0,0,0,0.4)'
    : '0 4px 16px rgba(0,0,0,0.12)'};
`;

const PlusMenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.875rem;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.15s ease;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

// Barra de digitação: esforço do raciocínio; o modelo em uso aparece no topo do menu.
const EffortButton = styled.button.attrs({ type: 'button' })`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 32px;
  padding: 0 8px 0 10px;
  margin-right: 2px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 0.875rem;
  font-family: 'Sora', 'Lexend', sans-serif;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  &:hover {
    background-color: ${({ theme }) => theme.hover_overlay_light};
    color: ${({ theme }) => theme.font_color};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const EffortMenu = styled.div`
  position: absolute;
  z-index: 1200;
  bottom: calc(100% + 6px);
  right: 0;
  padding: 4px 0;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.user_menu_border};
  background-color: ${({ theme }) => theme.sidebar_background_solid};
  min-width: 250px;
  box-shadow: ${({ theme }) => theme.mode === 'dark'
    ? '0 4px 16px rgba(0,0,0,0.4)'
    : '0 4px 16px rgba(0,0,0,0.12)'};
`;

const EffortMenuTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 14px 6px;
  font-size: 0.72rem;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) => theme.text_secondary};
  & > strong {
    font-size: 0.85rem;
    font-weight: 600;
    color: ${({ theme }) => theme.font_color};
  }
`;

const EffortOption = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 14px;
  cursor: pointer;
  text-align: left;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) => theme.font_color};
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const EffortOptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.85rem;
  & > small {
    font-size: 0.72rem;
    color: ${({ theme }) => theme.text_secondary};
  }
`;

const PlusMenuIcon = styled.span<{ $active?: boolean; $color?: string }>`
  display: flex;
  align-items: center;
  color: ${({ $active, $color, theme }) => $active ? ($color || theme.send_button_bg) : theme.font_color};
  opacity: ${({ $active }) => $active ? 0.9 : 0.5};
`;

// Pill flutuante sobre o composer, mesmo idioma de posicionamento do
// ScrollToBottomButton; $lifted sobe o pill quando o botão de scroll também
// está visível para os dois não se sobreporem.
const NoThinkFloatingButton = styled.button<{ $lifted?: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(${({ $lifted }) => ($lifted ? '-58px' : '-10px')});
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.model_selector_border};
  border-radius: 999px;
  background: ${({ theme }) => theme.model_selector_bg};
  color: ${({ theme }) => theme.font_color};
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Sora', 'Lexend', sans-serif;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.model_selector_hover_bg};
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const DeepResearchBadge = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 8px;
  font-size: 0.65rem;
  font-weight: 500;
  font-family: 'Sora', 'Lexend', sans-serif;
  background: ${({ $color }) => $color ? $color + '12' : 'rgba(25, 118, 210, 0.07)'};
  color: ${({ $color }) => $color || '#1976d2'};
`;

const ChatInputArea: React.FC<{ isLanding?: boolean }> = ({ isLanding }) => {
  const {
    isMobile,
    sidebarWidth,
    inputBottomOffset,
    isAtBottom,
    messageHistory,
    currentMessage,
    isRecording,
    status,
    loadingGeneration,
    setModelChat,
    sendingAudio,
    chatErrorMessage,
    modelChat,
    modelsWithFileUploadAllowed,
    reasoning,
    setReasoning,
    reasoningEffort,
    setReasoningEffort,
    reasoningEffortOptions,
    handleRespondWithoutThinking,
    messageBeingGenerated,
    loadingMessage,
    selectedFiles,
    setSelectedFiles,
    fileInputKey,
    audioTooltipOpen,
    fileInputRef,
    smoothScrollToEnd,
    handleSubmit,
    handleNewMessageInputChange,
    handleButtonDownTextInput,
    handleFileUpload,
    handleAudioButtonClick,
    handleStopClick,
    enqueueCurrentMessage,
    cancellingBackground,
    deepResearch,
    setDeepResearch,
    deepResearchMode,
    setDeepResearchMode,
    comparisonMode,
    setComparisonMode,
    userEmail,
    usePreciseLocation,
    togglePreciseLocation,
    setAudioTooltipOpen,
    renderSelectedFilesPreview,
    setCurrentMessage,
    setPromptBankOpen,
    dark,
    partialTranscript,
    sttTranscribing,
    modelDescriptions,
    dropdownMenuOptions,
    openDropdownMenu,
    setOpenDropdownMenu,
  } = usePlayground();
  const { deep_research: deepResearchEnabled, comparison_mode: comparisonModeEnabled } = useFeatureFlags();
  const isMaritacaUser = !!userEmail && userEmail.endsWith('@maritaca.ai');
  useRenderTracker('ChatInputArea', () => ({
    currentMessage, loadingGeneration, selectedFiles, isRecording, partialTranscript,
  }));

  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [effortMenuOpen, setEffortMenuOpen] = useState(false);

  const handleOpenPromptBank = () => {
    if (!chatErrorMessage.length) {
      setPromptBankOpen(true);
    }
  };

  const fileUploadAllowed = modelsWithFileUploadAllowed.includes(modelChat);
  // Pasting a large block of text → attach it as mensagem_N.txt instead of
  // inlining it (reuses the file-picker handler via a synthetic event).
  const handleMessagePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const images = clipboardImageFiles(e.clipboardData);
    if (images.length > 0 && fileUploadAllowed && chatErrorMessage.length === 0) {
      e.preventDefault();
      handleFileUpload({ target: { files: images } } as any);
      return;
    }

    const text = e.clipboardData?.getData('text/plain') ?? '';
    if (text.length <= PASTE_AS_FILE_MAX_CHARS) return; // small paste: normal inline behaviour
    if (!fileUploadAllowed) return;                     // model can't take files: leave it inline
    e.preventDefault();
    // Pick the next free mensagem_N.txt so repeated pastes don't collide.
    const existing = new Set((selectedFiles || []).map((f) => f.name));
    let i = 1;
    while (existing.has(`mensagem_${i}.txt`)) i += 1;
    const name = `mensagem_${i}.txt`;
    const file = new File([text], name, { type: 'text/plain' });
    handleFileUpload({ target: { files: [file] } } as any);
    // No toast — the attachment chip in the message bar is enough feedback.
  };

  const messageHistoryLength = messageHistory.length;
  return (
    <InputContainer
      $isMobile={isMobile}
      $sidebarWidth={sidebarWidth}
      $bottomOffset={inputBottomOffset}
      $isLanding={isLanding}
    >
      {!isAtBottom && messageHistoryLength > 0 && (
        <ScrollToBottomButton onClick={smoothScrollToEnd}>
          <StyledArrowDownwardIcon />
        </ScrollToBottomButton>
      )}
      {/* Pill "Resposta rápida": visível enquanto a geração está na fase
          de raciocínio (hint "Pensando...", ainda sem texto de resposta).
          O clique interrompe a geração e regenera o turno com o par sabia-4;
          o toggle Pensamento não muda. Fora do modo comparação — lá o stop/
          regenerate não se aplica aos streams cegos. */}
      {loadingGeneration && !cancellingBackground && !comparisonMode &&
        modelChat === 'sabia-4-thinking' && messageBeingGenerated.length === 0 &&
        loadingMessage === 'Pensando...' && (
        <NoThinkFloatingButton
          type="button"
          data-testid="responder-sem-pensar"
          $lifted={!isAtBottom && messageHistoryLength > 0}
          onClick={() => handleRespondWithoutThinking()}
        >
          <BoltIcon style={{ fontSize: '1rem' }} />
          Resposta rápida
        </NoThinkFloatingButton>
      )}
      <StylizedForm onSubmit={handleSubmit}>
        <UserInputContainer>
          <SubContainer>
            <InputSurface
              data-chat-file-drop-target="composer"
            >
              {deepResearch && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <DeepResearchBadge $color={getModelColor(modelChat, dark).primary}>
                    <TravelExploreIcon style={{ fontSize: '0.7rem' }} />
                    Pesquisa Aprofundada
                  </DeepResearchBadge>
                </div>
              )}
              {renderSelectedFilesPreview('input')}
              <ComposerPromptUI />
              <MessagesContainerArea>
                <InputTextArea
                  id="input-text-field"
                  multiline
                  placeholder={isRecording ? "Ouvindo..." : 'Envie uma mensagem ou digite "/" para prompts'}
                  onChange={handleNewMessageInputChange}
                  onPaste={handleMessagePaste}
                  value={isRecording ? (currentMessage + (partialTranscript ? (currentMessage ? ' ' : '') + partialTranscript : '')) : currentMessage}
                  maxRows={6}
                  onKeyDown={handleButtonDownTextInput}
                  disabled={chatErrorMessage.length > 0}
                />
              </MessagesContainerArea>
              <InputFooter>
                <InputActionsGroup>
                  {!isRecording && (
                    <ClickAwayListener onClickAway={() => { if (plusMenuOpen) setPlusMenuOpen(false); }} touchEvent={false}>
                      <PlusButton
                        data-testid="composer-plus"
                        onClick={() => setPlusMenuOpen(!plusMenuOpen)}
                        disabled={chatErrorMessage.length > 0}
                      >
                        <PlusIcon />
                        {plusMenuOpen && (
                          <PlusMenu onClick={(e) => e.stopPropagation()}>
                            {/* Pensamento define o MODELO (sabia-4-thinking vs sabia-4)
                                NO CHAT ATUAL — o backend permite a troca in-place desse par
                                (mesmo worker físico) e persiste o novo modelo no chat.
                                Fica no menu "+". */}
                            {['sabia-4', 'sabia-4-thinking'].includes(modelChat) && (
                              <PlusMenuItem
                                data-testid="toggle-thinking"
                                data-active={String(modelChat === 'sabia-4-thinking')}
                                aria-disabled={loadingGeneration}
                                style={{ opacity: loadingGeneration ? 0.48 : 1, cursor: loadingGeneration ? 'not-allowed' : 'pointer' }}
                                onClick={() => { if (!loadingGeneration) setModelChat(modelChat === 'sabia-4-thinking' ? 'sabia-4' : 'sabia-4-thinking'); }}
                              >
                                <PlusMenuIcon $active={modelChat === 'sabia-4-thinking'} $color={getModelColor(modelChat, dark).primary}><PsychologyIcon style={{ fontSize: '1.2rem' }} /></PlusMenuIcon>
                                Pensamento
                                <CustomTooltip
                                  title="Faz o assistente raciocinar passo a passo antes de responder. Melhor para problemas que exigem mais cuidado — matemática, lógica, análise — em troca de respostas um pouco mais lentas."
                                  placement="top"
                                >
                                  <InfoOutlinedIcon
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ fontSize: '0.95rem', marginLeft: 5, opacity: 0.55, cursor: 'help' }}
                                  />
                                </CustomTooltip>
                                <ToggleSwitch active={modelChat === 'sabia-4-thinking'} color={getModelColor(modelChat, dark).primary} />
                              </PlusMenuItem>
                            )}
                            {deepResearchEnabled && supportsDeepResearch(modelChat) && (
                              <PlusMenuItem data-testid="toggle-deep-research" onClick={() => { setDeepResearch(!deepResearch); }}>
                                <PlusMenuIcon $active={deepResearch} $color={getModelColor(modelChat, dark).primary}><TravelExploreIcon style={{ fontSize: '1.2rem' }} /></PlusMenuIcon>
                                Pesquisa Aprofundada
                                <CustomTooltip
                                  title="Investigação longa e estruturada: o assistente planeja, busca em várias fontes na web e organiza um relatório completo com as referências. Mais demorado, indicado para perguntas amplas que exigem pesquisa."
                                  placement="top"
                                >
                                  <InfoOutlinedIcon
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ fontSize: '0.95rem', marginLeft: 5, opacity: 0.55, cursor: 'help' }}
                                  />
                                </CustomTooltip>
                                <ToggleSwitch active={deepResearch} color={getModelColor(modelChat, dark).primary} />
                              </PlusMenuItem>
                            )}
                            {comparisonModeEnabled && isMaritacaUser && (
                              <PlusMenuItem onClick={() => { setComparisonMode(!comparisonMode); }}>
                                <PlusMenuIcon $active={comparisonMode} $color={getModelColor(modelChat, dark).primary}><CompareArrowsIcon style={{ fontSize: '1.2rem' }} /></PlusMenuIcon>
                                Modo comparação
                                <CustomTooltip
                                  title="Responde com o modelo selecionado E o competidor, lado a lado e às cegas, para você escolher a melhor resposta. As anotações são registradas para avaliação. Disponível apenas para @maritaca.ai."
                                  placement="top"
                                >
                                  <InfoOutlinedIcon
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ fontSize: '0.95rem', marginLeft: 5, opacity: 0.55, cursor: 'help' }}
                                  />
                                </CustomTooltip>
                                <ToggleSwitch active={comparisonMode} color={getModelColor(modelChat, dark).primary} />
                              </PlusMenuItem>
                            )}
                            <PlusMenuItem onClick={() => { togglePreciseLocation(); }}>
                              <PlusMenuIcon $active={usePreciseLocation} $color={getModelColor(modelChat, dark).primary}><MyLocationIcon style={{ fontSize: '1.2rem' }} /></PlusMenuIcon>
                              Localização precisa
                              <CustomTooltip
                                title="Usa a localização do navegador (GPS) em vez da aproximada por IP, para respostas com contexto local — clima, lugares e serviços da sua região. Enviada apenas junto com suas mensagens."
                                placement="top"
                              >
                                <InfoOutlinedIcon
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ fontSize: '0.95rem', marginLeft: 5, opacity: 0.55, cursor: 'help' }}
                                />
                              </CustomTooltip>
                              <ToggleSwitch active={usePreciseLocation} color={getModelColor(modelChat, dark).primary} />
                            </PlusMenuItem>
                            <PlusMenuItem onClick={() => { handleOpenPromptBank(); setPlusMenuOpen(false); }}>
                              <PlusMenuIcon><CategoryIcon style={{ fontSize: '1.2rem' }} /></PlusMenuIcon>
                              Biblioteca de prompts
                            </PlusMenuItem>
                            {modelsWithFileUploadAllowed.includes(modelChat) && (
                              <PlusMenuItem onClick={() => { fileInputRef.current?.click(); setPlusMenuOpen(false); }}>
                                <PlusMenuIcon><AttachFileIcon style={{ fontSize: '1.2rem' }} /></PlusMenuIcon>
                                Anexar arquivo
                              </PlusMenuItem>
                            )}
                          </PlusMenu>
                        )}
                        {/* No accept filter: an extension-based accept greys out
                            extension-less files in the OS picker (e.g. graph JSON
                            exports named without a .json suffix). The backend
                            content-validates by MIME (python-magic). */}
                        <input
                          key={fileInputKey}
                          ref={fileInputRef}
                          multiple
                          type="file"
                          onChange={handleFileUpload}
                          style={{ display: "none" }}
                          disabled={chatErrorMessage.length > 0}
                        />
                      </PlusButton>
                    </ClickAwayListener>
                  )}
                </InputActionsGroup>
                <InputActionsGroup>
                  {!isRecording && chatModelHasEffort(modelChat) && reasoningEffortOptions.length > 1 && (
                    <ClickAwayListener onClickAway={() => { if (effortMenuOpen) setEffortMenuOpen(false); }} touchEvent={false}>
                      <EffortButton
                        data-testid="reasoning-effort"
                        aria-haspopup="menu"
                        aria-expanded={effortMenuOpen}
                        onClick={() => setEffortMenuOpen(!effortMenuOpen)}
                        disabled={chatErrorMessage.length > 0}
                      >
                        {reasoningEffortLabel(reasoningEffort)}
                        <ExpandMoreIcon style={{ fontSize: '1.05rem', opacity: 0.7 }} />
                        {effortMenuOpen && (
                          <EffortMenu role="menu" onClick={(e) => e.stopPropagation()}>
                            <EffortMenuTitle data-testid="composer-model">
                              <strong>{chatModelLabel(modelChat)}</strong>
                              Esforço de raciocínio
                            </EffortMenuTitle>
                            {reasoningEffortOptions.map((option) => (
                              <EffortOption
                                key={option.value}
                                role="menuitemradio"
                                aria-checked={option.value === reasoningEffort}
                                $selected={option.value === reasoningEffort}
                                onClick={() => { setReasoningEffort(option.value); setEffortMenuOpen(false); }}
                              >
                                <EffortOptionText>
                                  {option.label}
                                  <small>{option.hint}</small>
                                </EffortOptionText>
                                {option.value === reasoningEffort
                                  ? <CheckCircleRoundedIcon style={{ fontSize: '1.1rem', color: getModelColor(modelChat, dark).primary }} />
                                  : <RadioButtonUncheckedRoundedIcon style={{ fontSize: '1.1rem', opacity: 0.35 }} />}
                              </EffortOption>
                            ))}
                          </EffortMenu>
                        )}
                      </EffortButton>
                    </ClickAwayListener>
                  )}
                  <CustomTooltip
                      title={status === "recording" ? "Parar gravação" : "Ditar texto"}
                      placement="top"
                      open={audioTooltipOpen}
                      onOpen={() => setAudioTooltipOpen(true)}
                      onClose={() => setAudioTooltipOpen(false)}
                    >
                      <AudioButton
                        className={status === "recording" ? "is-recording" : undefined}
                        onClick={handleAudioButtonClick}
                        disabled={sendingAudio || chatErrorMessage.length > 0}
                        aria-label={status === "recording" ? "Parar gravação" : "Gravar mensagem de voz"}
                        style={{
                          opacity: chatErrorMessage.length > 0 ? 0.5 : 1,
                          pointerEvents: chatErrorMessage.length > 0 ? "none" : "auto"
                        }}
                      >
                        {sendingAudio ? (
                          <CircularProgress size={24} />
                        ) : status === "recording" ? <StyledStopIcon /> : <StyledMicIcon />}
                      </AudioButton>
                    </CustomTooltip>
                  {/* Durante a geração, o rascunho pode entrar na fila: sai sozinho
                      quando a resposta terminar, sem interromper o modelo. */}
                  {!isRecording && loadingGeneration && !cancellingBackground && !comparisonMode &&
                    (currentMessage.trim().length > 0 || (selectedFiles && selectedFiles.length > 0)) && (
                    <CustomTooltip title="Enviar quando a resposta terminar" placement="top">
                      <EnqueueButton
                        type="button"
                        aria-label="enfileirar"
                        data-testid="enqueue-message"
                        $modelColor={getModelColor(modelChat, dark).primary}
                        $modelHover={getModelColor(modelChat, dark).accent}
                        onClick={enqueueCurrentMessage}
                        disabled={chatErrorMessage.length > 0 || sendingAudio}
                      >
                        <ScheduleSendIcon />
                      </EnqueueButton>
                    </CustomTooltip>
                  )}
                  {!isRecording && (loadingGeneration || currentMessage.trim().length > 0 || (selectedFiles && selectedFiles.length > 0)) && (
                    <CustomTooltip title={loadingGeneration ? "Parar" : "Enviar"} placement="top">
                      <StyledIconButton
                        type="button"
                        aria-label="send"
                        $modelColor={getModelColor(modelChat, dark).primary}
                        $modelHover={getModelColor(modelChat, dark).accent}
                        onClick={
                          loadingGeneration
                            ? handleStopClick
                            : handleSubmit
                        }
                        disabled={chatErrorMessage.length > 0 || sendingAudio || cancellingBackground}
                      >
                        {cancellingBackground ? <CircularProgress size={20} /> : loadingGeneration ? <StopIcon /> : <ArrowForwardIcon />}
                      </StyledIconButton>
                    </CustomTooltip>
                  )}
                </InputActionsGroup>
              </InputFooter>
            </InputSurface>
          </SubContainer>
        </UserInputContainer>
      </StylizedForm>

    </InputContainer>
  );
};

export default ChatInputArea;
